import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchFacebookSettings, type FacebookSettings } from '@/api/client';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function FacebookFeed() {
  const { t } = useTranslation('home');
  const [fb, setFb] = useState<FacebookSettings | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchFacebookSettings().then((data) => {
      if (cancelled) return;
      setFb(data);
    });
    return () => { cancelled = true; };
  }, []);

  const showFeed = fb?.show_feed === true || fb?.show_feed === 1;
  const hasEmbed = !!fb?.embed_code?.trim();
  const hasUrl = !!fb?.facebook_url?.trim();

  // Fallback: always show with the configured Facebook page URL
  const fallbackUrl = 'https://www.facebook.com/profile.php?id=61590138211063';
  const effectiveUrl = fb?.facebook_url?.trim() || fallbackUrl;
  const effectiveShow = showFeed || !fb;
  const effectiveHasUrl = hasUrl || !!fallbackUrl;

  if (!effectiveShow || (!hasEmbed && !effectiveHasUrl)) return null;

  return (
    <section id="facebook" className="w-full py-16 md:py-20 bg-brand-warm">
      <div className="px-4 md:px-6 lg:px-24 max-w-7xl mx-auto">
        {/* Section Header */}
        <ScrollReveal className="mb-10 md:mb-14 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-brand-mint" />
            <span className="text-sm font-medium tracking-wider uppercase text-brand-mint">
              {t('facebook_label')}
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-brand-dark font-heading leading-tight mb-4">
            {t('facebook_title')}
          </h2>
          <div className="w-10 h-1 bg-brand-mint rounded-full mx-auto" />
        </ScrollReveal>

        {hasEmbed ? (
          <ScrollReveal>
            <div
              className="max-w-3xl mx-auto bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100"
              dangerouslySetInnerHTML={{ __html: fb.embed_code }}
            />
          </ScrollReveal>
        ) : (
          <ScrollReveal>
            <div className="flex flex-col items-center gap-6">
              {/* Facebook Page Plugin iframe */}
              <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                <iframe
                  src={`https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(effectiveUrl)}&tabs=timeline&width=500&height=600&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true&appId`}
                  width="500"
                  height="600"
                  style={{ border: 'none', overflow: 'hidden', maxWidth: '100%' }}
                  scrolling="no"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  title="Facebook Page Feed"
                />
              </div>
              {/* Follow button */}
              <a
                href={effectiveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1877F2] text-white text-sm font-semibold rounded-full hover:bg-[#166fe5] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-facebook-fill" />
                {t('facebook_follow')}
              </a>
            </div>
          </ScrollReveal>
        )}
      </div>
    </section>
  );
}